import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MapPin, Filter, ChevronDown,
  Star, Video, Globe, Users,
  Briefcase, Calendar, CheckCircle, Circle, X, ArrowRight, Clock, Monitor, Quote, Languages,
  User, MessageCircle, Compass, Sparkles, Lock, LogIn, AlertTriangle, ShieldCheck,
  ChevronLeft, CreditCard, RefreshCcw
} from 'lucide-react';
import { studentBookingApi, loadRazorpayScript, openRazorpayCheckout } from '../../../services/counselorApi2';

// ─── QUOTES ──────────────────────────────────────────────────────────────────
const quotes = [
  { text: "Guidance is not about creating a path for the student, but about lighting the lamp so they can see their own way.", author: "Vidyazon Vision" },
  { text: "The influence of a good counselor can never be erased. Join us in shaping the future of education.", author: "Education Matters" },
  { text: "Empower the next generation. Your expertise can turn a student's confusion into clarity and confidence.", author: "Future Leaders" },
  { text: "Connect with thousands of students looking for mentorship. Your knowledge is the key to their success.", author: "Vidyazon Partner Program" },
  { text: "Be the bridge between dreams and reality. Help students navigate the complex world of career choices.", author: "Career Impact" },
  { text: "Every student has a story; we help them write a successful new chapter.", author: "Vidyazon" },
  { text: "Clarity is power. We provide the compass for your academic and career journey.", author: "Vidyazon Guidance" },
  { text: "Greatness starts with a great plan. Find the experts who can craft yours.", author: "Strategic Success" },
  { text: "Your potential is infinite. A little direction is all you need.", author: "Limitless Learning" },
  { text: "We connect aspiration with achievement, one session at a time.", author: "Vidyazon" },
];

// ─── AUTH HELPERS ─────────────────────────────────────────────────────────────
const getLoggedInStudent = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const raw = localStorage.getItem('user');
    if (raw) {
      const u = JSON.parse(raw);
      // block non-students
      if (u.role && u.role !== 'student') return null;
      return u;
    }
  } catch (e) {}
  return token ? { token } : null;
};

// ─── LOGIN REQUIRED MODAL ─────────────────────────────────────────────────────
function StudentLoginRequired({ counselorName, onClose }) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 p-6 text-white text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-extrabold">Student Login Required</h2>
          <p className="text-indigo-100 text-sm mt-1.5">
            {counselorName
              ? `To book a session with ${counselorName}, please login as a student.`
              : 'Please login as a student to book a counseling session.'}
          </p>
        </div>
        <div className="p-5 space-y-3">
          <button
            onClick={() => navigate('/login?type=student&redirect=/services/counselling')}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition"
          >
            <LogIn className="w-4 h-4" /> Login as Student
          </button>
          <button
            onClick={() => navigate('/signup?type=student')}
            className="w-full py-3 border-2 border-indigo-100 text-indigo-700 rounded-xl font-bold hover:bg-indigo-50 transition"
          >
            Create Student Account
          </button>
          <button onClick={onClose} className="w-full py-2 text-gray-400 text-sm hover:text-gray-600 transition">
            Continue Browsing
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FILTER HELPERS ───────────────────────────────────────────────────────────
const FilterSelect = ({ label, value, onChange, options, defaultOption }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
    <select value={value} onChange={onChange}
      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none bg-white transition">
      <option value="">{defaultOption}</option>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

const FilterField = ({ icon: Icon, label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{label}</label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input type={type} placeholder={placeholder} value={value} onChange={onChange}
        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition" />
    </div>
  </div>
);

// ─── COUNSELOR CARD ───────────────────────────────────────────────────────────
function CounselorCard({ counselor, onViewProfile }) {
  const profileImage = counselor.photo_url ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(counselor.contact_person || 'C')}&background=6366f1&color=fff&bold=true`;

  const getDisplayPrice = () => {
    if (counselor.pricing && typeof counselor.pricing === 'object') {
      const prices = Object.values(counselor.pricing).filter(p => p > 0);
      if (prices.length > 0) return Math.min(...prices);
    }
    return counselor.consultation_fee || 500;
  };

  return (
    <div className="bg-white rounded-2xl shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 border border-indigo-50 flex flex-col h-full overflow-hidden group cursor-pointer">

      {/* Card top strip */}
      <div className="h-1 w-full bg-gradient-to-r from-indigo-400 via-purple-500 to-pink-400" />

      {/* Profile Section */}
      <div className="p-5 flex gap-4 border-b border-gray-100">
        <div className="flex-shrink-0 relative">
          <div className="w-20 h-20 rounded-2xl overflow-hidden border-2 border-indigo-100 group-hover:border-purple-400 transition-colors shadow-sm">
            <img
              src={profileImage}
              alt={counselor.contact_person}
              className="w-full h-full object-cover"
              onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(counselor.contact_person || 'C')}&background=6366f1&color=fff`; }}
            />
          </div>
          {(counselor.mode === 'Online' || counselor.mode === 'Both') && (
            <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-white rounded-full flex items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-1">
            <h3 className="text-base font-extrabold text-gray-900 truncate group-hover:text-indigo-600 transition-colors leading-tight">
              {counselor.contact_person}
            </h3>
            <div className="flex-shrink-0 flex items-center gap-0.5 text-xs font-bold bg-amber-50 text-amber-600 px-2 py-0.5 rounded-lg border border-amber-100">
              <Star className="w-3 h-3 fill-current" /> 4.9
            </div>
          </div>
          <p className="text-indigo-600 text-xs font-bold uppercase tracking-wide mt-1 truncate">
            {counselor.category || 'Counselor'}
          </p>
          <p className="text-gray-400 text-xs truncate mt-0.5">{counselor.organization_name}</p>
        </div>
      </div>

      {/* Details */}
      <div className="p-5 flex-grow space-y-3">
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-700 text-[10px] font-bold border border-blue-100">
            <Briefcase className="w-3 h-3" /> {counselor.experience_years || '1'}+ Yrs
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100">
            <Monitor className="w-3 h-3" /> {counselor.mode || 'Online'}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 text-gray-600 text-[10px] font-bold border border-gray-200">
            <MapPin className="w-3 h-3" /> {counselor.location || 'Remote'}
          </span>
        </div>
        <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
          {counselor.bio || 'Dedicated professional helping students achieve their academic and career goals.'}
        </p>
        {counselor.languages && counselor.languages.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Languages className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{counselor.languages.slice(0, 3).join(' · ')}{counselor.languages.length > 3 ? ' +more' : ''}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 bg-gradient-to-r from-indigo-50/70 to-purple-50/70 border-t border-indigo-100">
        <div className="flex justify-between items-center mb-3">
          <div>
            <p className="text-[10px] text-indigo-500 uppercase font-bold tracking-wider">Starts From</p>
            <div className="flex items-baseline gap-0.5 text-gray-900 font-extrabold text-xl leading-none mt-0.5">
              <span className="text-green-600 text-sm font-bold">₹</span>{getDisplayPrice()}
            </div>
          </div>
          <span className="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-500 text-[10px] px-2 py-1.5 rounded-lg font-semibold shadow-sm">
            <Clock className="w-3 h-3" /> {counselor.availability_type || 'Flexible'}
          </span>
        </div>
        <button
          onClick={onViewProfile}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-md shadow-indigo-200/60 group-hover:shadow-lg"
        >
          View Profile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
}

// ─── 3-STEP BOOKING MODAL WITH RAZORPAY ──────────────────────────────────────
function BookingModal({ counselor, user, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [problemDescription, setProblemDescription] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => { generateDates(); }, []);

  const generateDates = () => {
    const dates = [];
    const today = new Date();
    const daysMap = {};
    counselor.available_slots?.forEach(slot => {
      if (!daysMap[slot.day]) daysMap[slot.day] = [];
      daysMap[slot.day].push(`${slot.start_time} - ${slot.end_time}`);
    });
    for (let i = 1; i <= 30; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
      if (daysMap[dayName]) {
        dates.push({
          fullDate: d,
          dateString: d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
          dayName,
          slots: daysMap[dayName],
        });
      }
    }
    setAvailableDates(dates);
  };

  const pricingOptions = counselor.pricing
    ? Object.entries(counselor.pricing).filter(([, p]) => p > 0).map(([type, price]) => ({ type, price }))
    : [{ type: 'Standard Session', price: counselor.consultation_fee || 999 }];

  const handlePayAndBook = async () => {
    setPayError('');
    setPaying(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Razorpay failed to load. Check your internet connection.');

      const orderData = await studentBookingApi.createBookingOrder({
        counselorId: counselor._id,
        preferredSlot: `${selectedDate.dateString} · ${selectedTime}`,
        problemDescription,
      });
      if (!orderData.success) throw new Error(orderData.message || 'Failed to create order');

      const payResult = await openRazorpayCheckout(
        { key_id: orderData.key_id, order_id: orderData.order_id, amount: orderData.amount },
        { name: user?.fullName || '', email: user?.email || '', contact: user?.mobile || '' }
      );

      const verified = await studentBookingApi.verifyBookingPayment({
        orderId: payResult.razorpay_order_id,
        paymentId: payResult.razorpay_payment_id,
        signature: payResult.razorpay_signature,
      });

      if (verified.success) {
        onSuccess(verified.booking_id || 'CONFIRMED');
      } else {
        throw new Error('Payment verification failed. Contact support.');
      }
    } catch (err) {
      setPayError(err.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const stepTitles = ['Select Plan', 'Pick Date & Time', 'Confirm & Pay'];

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col max-h-[88vh] overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-5 text-white flex justify-between items-center flex-shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'bg-white w-8' : 'bg-white/30 w-4'}`} />
              ))}
            </div>
            <h3 className="text-lg font-extrabold">{stepTitles[step - 1]}</h3>
            <p className="text-indigo-200 text-xs mt-0.5">Step {step} of 3 · {counselor.contact_person}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* STEP 1: PLAN */}
          {step === 1 && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">Choose the plan that best suits your needs.</p>
              {pricingOptions.map((option, idx) => (
                <div key={idx} onClick={() => setSelectedPlan(option)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all flex justify-between items-center ${selectedPlan?.type === option.type ? 'border-indigo-600 bg-indigo-50 shadow-sm' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    {selectedPlan?.type === option.type
                      ? <CheckCircle className="w-5 h-5 text-indigo-600" />
                      : <Circle className="w-5 h-5 text-gray-300" />}
                    <div>
                      <span className={`font-semibold text-sm ${selectedPlan?.type === option.type ? 'text-indigo-900' : 'text-gray-700'}`}>{option.type}</span>
                    </div>
                  </div>
                  <span className="text-xl font-extrabold text-gray-900">₹{option.price}</span>
                </div>
              ))}
            </div>
          )}

          {/* STEP 2: DATE & TIME */}
          {step === 2 && (
            <div className="space-y-5">
              <div>
                <h4 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-500" /> Available Dates (Next 30 days)
                </h4>
                {availableDates.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                    <p className="text-amber-700 text-sm font-medium">No scheduled slots available. Contact the counselor directly.</p>
                  </div>
                ) : (
                  <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-indigo-200">
                    {availableDates.map((d, idx) => (
                      <button key={idx}
                        onClick={() => { setSelectedDate(d); setSelectedTime(null); }}
                        className={`flex-shrink-0 w-[68px] p-2.5 rounded-xl border-2 text-center transition-all ${selectedDate?.dateString === d.dateString ? 'border-indigo-600 bg-indigo-600 text-white shadow-md' : 'border-gray-200 hover:border-indigo-300 text-gray-700 bg-white'}`}>
                        <span className="block text-[10px] font-bold uppercase opacity-80">{d.dayName.substr(0, 3)}</span>
                        <span className="block text-xl font-extrabold mt-0.5">{d.fullDate.getDate()}</span>
                        <span className="block text-[9px] opacity-70 mt-0.5">{d.dateString.split(' ')[1]}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {selectedDate && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-500" /> Select Time Slot
                  </h4>
                  <div className="grid grid-cols-2 gap-2.5">
                    {selectedDate.slots.map((time, idx) => (
                      <button key={idx} onClick={() => setSelectedTime(time)}
                        className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${selectedTime === time ? 'bg-green-500 border-green-500 text-white shadow-md' : 'bg-white border-gray-200 hover:border-indigo-300 text-gray-700'}`}>
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Describe Your Concern <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea rows={3} value={problemDescription} onChange={e => setProblemDescription(e.target.value)}
                  placeholder="What would you like guidance on? Share briefly..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 outline-none resize-none transition"
                />
              </div>
            </div>
          )}

          {/* STEP 3: CONFIRM */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 border border-indigo-100">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" /> Booking Summary
                </h4>
                {[
                  { icon: User, label: 'Counselor', value: counselor.contact_person },
                  { icon: Calendar, label: 'Date', value: selectedDate?.dateString },
                  { icon: Clock, label: 'Time', value: selectedTime },
                  { icon: Briefcase, label: 'Plan', value: selectedPlan?.type },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-indigo-100 last:border-0">
                    <span className="text-gray-500 text-sm flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-indigo-400" />{label}</span>
                    <span className="font-semibold text-gray-900 text-sm">{value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-3 mt-1">
                  <span className="font-extrabold text-gray-900">Total Amount</span>
                  <span className="text-3xl font-black text-green-600">₹{selectedPlan?.price}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 rounded-xl p-3 border border-gray-100">
                <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
                Secured by Razorpay · 100% safe and encrypted payment
              </div>

              {payError && (
                <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 rounded-xl p-3 border border-red-200">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500" />
                  {payError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-100 bg-gray-50 flex-shrink-0 flex gap-3">
          {step > 1 && !paying && (
            <button onClick={() => setStep(s => s - 1)} className="px-4 py-3 text-gray-600 font-bold hover:bg-gray-200 rounded-xl transition flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}

          {step === 1 && (
            <button onClick={() => selectedPlan && setStep(2)} disabled={!selectedPlan}
              className={`flex-1 py-3.5 rounded-xl font-bold text-white transition shadow-lg ${selectedPlan ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`}>
              Continue →
            </button>
          )}

          {step === 2 && (
            <button onClick={() => selectedTime && setStep(3)} disabled={!selectedTime}
              className={`flex-1 py-3.5 rounded-xl font-bold text-white transition shadow-lg ${selectedTime ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`}>
              Review Booking →
            </button>
          )}

          {step === 3 && (
            <button onClick={handlePayAndBook} disabled={paying}
              className={`flex-1 py-3.5 rounded-xl font-bold text-white transition shadow-lg flex items-center justify-center gap-2 ${paying ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}>
              {paying
                ? <><RefreshCcw className="w-4 h-4 animate-spin" /> Processing Payment...</>
                : <><CreditCard className="w-4 h-4" /> Pay ₹{selectedPlan?.price} Securely</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── COUNSELOR DETAIL VIEW ────────────────────────────────────────────────────
function CounselorDetailView({ counselor, onBack }) {
  const navigate = useNavigate();
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [bookingDone, setBookingDone] = useState(null);

  const loggedInUser = getLoggedInStudent();

  const handleBookClick = () => {
    if (!loggedInUser) {
      // save intent and show login prompt
      sessionStorage.setItem('pending_booking_counselor', counselor._id);
      setShowLoginModal(true);
    } else {
      setShowBookingModal(true);
    }
  };

  const displayPrice = counselor.pricing
    ? Math.min(...Object.values(counselor.pricing).filter(p => p > 0))
    : (counselor.consultation_fee || 500);

  // ── Success screen ──
  if (bookingDone) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24 flex items-center justify-center px-4">
        <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center border border-green-100">
          <div className="w-20 h-20 rounded-full bg-green-100 border-4 border-green-200 flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Booking Confirmed! 🎉</h2>
          <p className="text-gray-500 mb-1 text-sm">Your session with <span className="font-bold text-indigo-700">{counselor.contact_person}</span> is confirmed.</p>
          <p className="text-xs text-gray-400 mb-8 bg-gray-50 rounded-lg p-2 inline-block mt-1">Booking ID: <span className="font-mono font-bold">{bookingDone}</span></p>
          <div className="space-y-3">
            <button onClick={() => navigate('/student/dashboard')}
              className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition">
              View My Dashboard
            </button>
            <button onClick={onBack}
              className="w-full py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition">
              Browse More Counselors
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        <button onClick={onBack} className="mb-6 flex items-center gap-2 text-gray-500 hover:text-indigo-600 font-medium transition group">
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back to Search
        </button>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Banner */}
          <div className="h-40 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10" />
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)' }} />
          </div>

          {/* Profile header */}
          <div className="relative px-8 pt-0 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-16 mb-6">
              <div className="w-32 h-32 rounded-2xl overflow-hidden border-4 border-white shadow-xl bg-white flex-shrink-0">
                <img
                  src={counselor.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(counselor.contact_person || 'C')}&background=6366f1&color=fff&size=128&bold=true`}
                  alt={counselor.contact_person}
                  className="w-full h-full object-cover"
                  onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(counselor.contact_person || 'C')}&background=6366f1&color=fff`; }}
                />
              </div>
              <div className="text-gray-900 pb-1">
                <h2 className="text-2xl font-extrabold leading-tight">{counselor.contact_person}</h2>
                <p className="text-indigo-600 font-bold">{counselor.category}</p>
                <p className="text-gray-400 text-sm">{counselor.organization_name}</p>
              </div>
            </div>
          </div>

          <div className="px-8 pb-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left sidebar */}
            <div className="lg:col-span-1 space-y-5">
              {/* Price & Book */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-2xl border border-indigo-100 text-center">
                <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-1">Consultation Starts At</p>
                <div className="text-4xl font-black text-gray-900 mb-1">
                  <span className="text-green-600 text-2xl">₹</span>{displayPrice}
                </div>
                <p className="text-xs text-gray-400 mb-4">per session</p>
                <button onClick={handleBookClick}
                  className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-bold hover:shadow-xl shadow-indigo-200 transition transform hover:scale-[1.02] flex items-center justify-center gap-2">
                  {!loggedInUser ? <><Lock className="w-4 h-4" /> Login to Book</> : <><Video className="w-4 h-4" /> Book Appointment</>}
                </button>
              </div>

              {/* Stats */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3 shadow-sm">
                <h4 className="font-extrabold text-gray-800 text-sm border-b pb-2">Professional Summary</h4>
                {[
                  { icon: Briefcase, color: 'text-purple-400', label: 'Experience', value: `${counselor.experience_years || '1'}+ Years` },
                  { icon: Monitor, color: 'text-blue-400', label: 'Mode', value: counselor.mode || 'Online' },
                  { icon: Users, color: 'text-pink-400', label: 'Category', value: counselor.category || 'Counselor' },
                  { icon: MapPin, color: 'text-orange-400', label: 'Location', value: counselor.location || 'Remote' },
                ].map(({ icon: Icon, color, label, value }) => (
                  <div key={label} className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 flex items-center gap-2"><Icon className={`w-4 h-4 ${color}`} />{label}</span>
                    <span className="font-semibold text-gray-900 text-right max-w-[120px] truncate">{value}</span>
                  </div>
                ))}
                {counselor.languages?.length > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500 flex items-center gap-2"><Languages className="w-4 h-4 text-teal-400" />Languages</span>
                    <span className="font-semibold text-gray-900 text-right text-xs">{counselor.languages.slice(0, 3).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Right content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Bio */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                <h3 className="text-lg font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-500" /> Profile & Bio
                </h3>
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap text-sm">
                  {counselor.bio || 'Dedicated professional helping students achieve their academic and career goals with personalized guidance and expert advice.'}
                </p>
              </div>

              {/* Expertise */}
              {counselor.expertise?.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6">
                  <h3 className="text-lg font-extrabold text-gray-900 mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-indigo-500" /> Expertise & Concerns
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {counselor.expertise.map((exp, i) => (
                      <span key={i} className="px-3.5 py-2 bg-indigo-50 text-indigo-700 rounded-xl text-xs font-bold border border-indigo-100">
                        {exp}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="text-lg font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-purple-500" /> Available Schedule
                </h3>
                {counselor.available_slots?.length > 0 ? (
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {counselor.available_slots.map((slot, i) => (
                      <div key={i} className="flex-shrink-0 w-32 p-3.5 bg-indigo-50 border border-indigo-100 rounded-xl text-center">
                        <p className="font-extrabold text-gray-900 text-sm">{slot.day}</p>
                        <p className="text-xs text-indigo-600 mt-1 font-medium">{slot.start_time}–{slot.end_time}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm">Please contact the counselor for custom timing arrangements.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showLoginModal && (
        <StudentLoginRequired counselorName={counselor.contact_person} onClose={() => setShowLoginModal(false)} />
      )}
      {showBookingModal && loggedInUser && (
        <BookingModal
          counselor={counselor}
          user={loggedInUser}
          onClose={() => setShowBookingModal(false)}
          onSuccess={(id) => { setShowBookingModal(false); setBookingDone(id); }}
        />
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function CounselingBrowsePage() {
  const navigate = useNavigate();
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [fadeQuote, setFadeQuote] = useState(true);

  const loggedInUser = getLoggedInStudent();

  const [filters, setFilters] = useState({
    search: '', location: '', category: '', concern: '',
    language: '', mode: '', gender: '', minRating: 0,
    availability: '', minExperience: 0,
  });

  const categoryOptions = ['Education Counsellor', 'Guidance Counsellor', 'Study Abroad Counsellor', 'Admission Counsellor', 'Student Career Counsellor', 'Mental Health Counsellor', 'Child Psychology Counsellor', 'Relationship Counsellor', 'Career Counsellor'];
  const concernOptions = ['Personal / Social', 'Career / Stream', 'Clinical', 'Workplace / Professional', 'Critical', 'Others'];
  const languageOptions = ['English', 'Hindi', 'Marathi', 'Bengali', 'Tamil', 'Telugu', 'Kannada', 'Gujarati'];
  const modeOptions = ['Online', 'Offline', 'Both'];
  const availabilityOptions = ['Weekday', 'Weekend', 'Flexible'];

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeQuote(false);
      setTimeout(() => { setQuoteIndex(prev => (prev + 1) % quotes.length); setFadeQuote(true); }, 500);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    loadCounselors();
  }, [
    debouncedSearch,
    filters.location,
    filters.category,
    filters.concern,
    filters.language,
    filters.mode,
    filters.gender,
    filters.minRating,
    filters.availability,
    filters.minExperience
  ]);
  

  const loadCounselors = async () => {
    setLoading(true);
    try {
      const response = await studentBookingApi.getPublishedCounselors(filters);

setCounselors(Array.isArray(response) ? response : response.counselors || []);

    } catch (error) {
      console.error('Failed to load counselors:', error);
      setCounselors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({ search: '', location: '', category: '', concern: '', language: '', mode: '', gender: '', minRating: 0, availability: '', minExperience: 0 });

  // ── If a counselor is selected, show detail view ──
  if (selectedCounselor) {
    return <CounselorDetailView counselor={selectedCounselor} onBack={() => setSelectedCounselor(null)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Auth Banner */}
        {!loggedInUser ? (
          <div className="mb-6 flex items-center justify-between gap-4 bg-indigo-50 border border-indigo-200 rounded-xl px-5 py-3">
            <div className="flex items-center gap-3 text-sm text-indigo-800 min-w-0">
              <Lock className="w-4 h-4 text-indigo-500 flex-shrink-0" />
              <span className="truncate">Login as a <strong>student</strong> to book sessions with our expert counselors.</span>
            </div>
            <button onClick={() => setShowLoginModal(true)}
              className="flex-shrink-0 px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold hover:bg-indigo-700 transition flex items-center gap-1.5">
              <LogIn className="w-3.5 h-3.5" /> Login
            </button>
          </div>
        ) : (
          <div className="mb-6 flex items-center justify-between gap-4 bg-green-50 border border-green-200 rounded-xl px-5 py-3">
            <div className="flex items-center gap-3 text-sm text-green-800">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
              <span>Welcome, <strong>{loggedInUser?.fullName || loggedInUser?.email || 'Student'}</strong>! Click any counselor to view & book a session.</span>
            </div>
            <button onClick={() => navigate('/student/dashboard')}
              className="flex-shrink-0 px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition whitespace-nowrap">
              My Sessions →
            </button>
          </div>
        )}

        {/* Search & Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 mb-8">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, organization, or keywords..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition text-base"
              />
            </div>
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition ${showFilters ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-700">Refine Results</h3>
                <button onClick={clearFilters} className="text-indigo-600 hover:text-indigo-700 text-sm font-semibold">Reset All</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <FilterField icon={MapPin} label="Location" value={filters.location} onChange={e => handleFilterChange('location', e.target.value)} placeholder="City..." />
                <FilterSelect label="Category" value={filters.category} onChange={e => handleFilterChange('category', e.target.value)} options={categoryOptions} defaultOption="All Categories" />
                <FilterSelect label="Concern" value={filters.concern} onChange={e => handleFilterChange('concern', e.target.value)} options={concernOptions} defaultOption="All Concerns" />
                <FilterSelect label="Language" value={filters.language} onChange={e => handleFilterChange('language', e.target.value)} options={languageOptions} defaultOption="Any Language" />
                <FilterSelect label="Mode" value={filters.mode} onChange={e => handleFilterChange('mode', e.target.value)} options={modeOptions} defaultOption="Any Mode" />
                <FilterSelect label="Gender" value={filters.gender} onChange={e => handleFilterChange('gender', e.target.value)} options={['Male', 'Female']} defaultOption="Any Gender" />
                <FilterSelect label="Availability" value={filters.availability} onChange={e => handleFilterChange('availability', e.target.value)} options={availabilityOptions} defaultOption="Any Time" />
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
                    Experience: <span className="text-gray-900">{filters.minExperience}+ Years</span>
                  </label>
                  <input type="range" min="0" max="20" step="1" value={filters.minExperience}
                    onChange={e => handleFilterChange('minExperience', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600" />
                  <div className="flex justify-between text-xs text-gray-400 mt-1"><span>0 Yr</span><span>20+ Yr</span></div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Min Rating</label>
                  <div className="flex items-center gap-1 py-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button key={star} onClick={() => handleFilterChange('minRating', star)}
                        className={`p-1 transition focus:outline-none ${filters.minRating >= star ? 'text-yellow-400 scale-110' : 'text-gray-300'}`}>
                        <Star className={`w-5 h-5 ${filters.minRating >= star ? 'fill-yellow-400' : ''}`} />
                      </button>
                    ))}
                    {filters.minRating > 0 && (
                      <button onClick={() => handleFilterChange('minRating', 0)} className="text-gray-400 hover:text-gray-600 ml-1">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="mb-5 flex items-center justify-between">
          <p className="text-gray-600 font-semibold text-base">
            {loading ? (
              <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-indigo-400 border-t-transparent animate-spin inline-block" /> Searching...</span>
            ) : (
              <><span className="text-indigo-700 font-extrabold">{counselors.length}</span> Counselors Found</>
            )}
          </p>
        </div>

        {/* Counselors Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-14 h-14 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin mb-5" />
            <p className="text-gray-500 font-medium">Finding the best matches...</p>
          </div>
        ) : counselors.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-16 text-center">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2">No Counselors Found</h3>
            <p className="text-gray-500 mb-6 text-sm">Try adjusting your filters to discover more counselors.</p>
            <button onClick={clearFilters} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition shadow-md">
              Clear All Filters
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12">
            {counselors.map((counselor, index) => (
              <CounselorCard
                key={counselor._id || index}
                counselor={counselor}
                onViewProfile={() => setSelectedCounselor(counselor)}
              />
            ))}
          </div>
        )}

        {/* Branding Section */}
        <div className="space-y-8 mt-4 border-t border-gray-200 pt-12">
          <div className="relative overflow-hidden bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 rounded-3xl p-8 text-white shadow-xl">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="space-y-4 max-w-2xl text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold tracking-wide uppercase">
                  <Sparkles className="w-3 h-3 text-yellow-300" /> Premium Mentorship
                </div>
                <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                  Navigate Your Future with{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 to-pink-200">Expert Guidance</span>
                </h1>
                <p className="text-indigo-100 text-lg opacity-90">
                  Connect with certified counselors to find clarity in your career, academics, and personal growth.
                </p>
              </div>
              <div className="relative w-full max-w-xs h-32 flex items-center justify-center">
                <div className="absolute z-20 bg-white p-4 rounded-full shadow-lg shadow-indigo-900/50 animate-pulse">
                  <Compass className="w-8 h-8 text-indigo-600" />
                </div>
                <div className="absolute left-10 top-0 animate-bounce delay-75 bg-purple-500/30 backdrop-blur-md p-2 rounded-xl">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div className="absolute right-10 top-2 animate-bounce delay-150 bg-pink-500/30 backdrop-blur-md p-2 rounded-xl">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="absolute bottom-0 animate-bounce delay-300 bg-blue-500/30 backdrop-blur-md p-2 rounded-xl">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-white/20 to-transparent top-1/2" />
              </div>
            </div>
          </div>

          <div className="relative max-w-4xl mx-auto overflow-hidden rounded-2xl bg-gradient-to-r from-gray-900 via-indigo-900 to-gray-900 p-0.5 shadow-xl">
            <div className="bg-gray-900 rounded-xl p-8 text-white text-center relative h-36 flex flex-col justify-center items-center">
              <Quote className="absolute top-4 left-6 w-10 h-10 text-white/10" />
              <div className={`transition-all duration-700 transform ${fadeQuote ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <p className="text-lg md:text-xl font-serif italic mb-3 tracking-wide text-gray-100">"{quotes[quoteIndex].text}"</p>
                <p className="text-xs font-bold uppercase tracking-widest text-indigo-400">— {quotes[quoteIndex].author}</p>
              </div>
              <Quote className="absolute bottom-4 right-6 w-10 h-10 text-white/10 rotate-180" />
            </div>
          </div>
        </div>
      </div>

      {showLoginModal && <StudentLoginRequired onClose={() => setShowLoginModal(false)} />}
    </div>
  );
}